import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import download from 'downloadjs';
import Loading from '../common/Loading';
import PdfPreviewModal from './PdfPreviewModal';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.45);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 20px;
  width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  padding: 36px;
  position: relative;
  animation: fadeIn 0.2s ease;
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @media (max-width: 768px) {
    width: 92vw;
    padding: 24px 18px;
  }
`;

const CloseBtn = styled.div`
  position: absolute;
  top: 18px;
  right: 20px;
  cursor: pointer;
  font-size: 24px;
  color: #999;
  &:hover { color: #333; }
`;

const Title = styled.h3`
  margin: 0 0 24px 0;
  font-size: 20px;
  font-weight: 700;
`;

const InfoRow = styled.div`
  display: flex;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: 15px;
`;

const Label = styled.div`
  width: 120px;
  font-weight: 600;
  color: #555;
  flex-shrink: 0;
`;

const Value = styled.div`
  flex: 1;
`;

const SectionTitle = styled.div`
  font-weight: 600;
  margin-top: 24px;
  margin-bottom: 10px;
  font-size: 16px;
`;

const ItemTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  th, td {
    padding: 10px 12px;
    text-align: center;
    border-bottom: 1px solid #eee;
    font-size: 14px;
    white-space: nowrap;
  }
  th:first-child, td:first-child {
    white-space: normal;
    text-align: left;
  }
  thead tr {
    background: #333;
    color: #fff;
  }
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 24px;
`;

const PdfBtn = styled.button`
  flex: 1;
  padding: 12px;
  border: 1.5px solid ${({ $primary }) => ($primary ? '#333' : '#ddd')};
  border-radius: 10px;
  background: ${({ $primary }) => ($primary ? '#333' : '#fafafa')};
  font-size: 14px;
  font-weight: 600;
  color: ${({ $primary }) => ($primary ? '#fff' : '#555')};
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: ${({ $primary }) => ($primary ? '#111' : '#f0f0f0')};
    border-color: ${({ $primary }) => ($primary ? '#111' : '#ccc')};
  }
`;

const OrderDetailModal = ({ userId, orderId, orderIdStr, onClose }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPdf, setShowPdf] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    const fetchDetail = async () => {
      try {
        const { data } = await axios.get(`/api/orders/orderlist/detail/${userId}/${orderId}`);
        setOrder(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchDetail();
  }, [userId, orderId]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleDownload = async () => {
    try {
      const { data: res } = await axios.get(`/api/orders/orderlist/download/${userId}/${orderId}`);
      download(res, `슈퍼트랙_발주서_${orderIdStr}.pdf`, 'application/pdf');
    } catch (e) {
      console.error(e);
    }
  };

  const formatTime = (time) => {
    if (!time) return '-';
    return time.replace(/(\d{4})\.(\d{2})\.(\d{2})-?(\d{2}):(\d{2}):(\d{2})/, (_, y, m, d, h, mi, s) => `${y.slice(2)}/${m}/${d} ${h}:${mi}`);
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalBox>
        <CloseBtn onClick={onClose}>&times;</CloseBtn>
        {loading ? (
          <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loading />
          </div>
        ) : !order ? (
          <div>주문 정보를 불러올 수 없습니다.</div>
        ) : (
          <>
            <Title>주문 상세</Title>
            {order.deleted && (
              <div style={{
                padding: '10px 14px',
                marginBottom: 16,
                borderRadius: 10,
                background: '#fef2f2',
                color: '#dc2626',
                fontSize: 14,
                fontWeight: 600,
              }}>
                이 주문은 취소되었습니다.
              </div>
            )}
            <InfoRow>
              <Label>발주번호</Label>
              <Value>{order.order_id}</Value>
            </InfoRow>
            <InfoRow>
              <Label>발주일시</Label>
              <Value>{formatTime(order.time)}</Value>
            </InfoRow>
            <InfoRow>
              <Label>운송장 번호</Label>
              <Value>{order.postNum}</Value>
            </InfoRow>

            {order.items && order.items.length > 0 ? (
              <>
                <SectionTitle>주문 상품</SectionTitle>
                <ItemTable>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>상품명</th>
                      <th>수량</th>
                      <th>단가</th>
                      <th>소계</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, i) => (
                      <tr key={i}>
                        <td style={{ textAlign: 'left' }}>{item.product_name}</td>
                        <td>{item.count}</td>
                        <td>{item.unit_price?.toLocaleString('ko-KR')}원</td>
                        <td>{(item.count * item.unit_price).toLocaleString('ko-KR')}원</td>
                      </tr>
                    ))}
                  </tbody>
                  {order.shipping_fee > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan="3" style={{ textAlign: 'right', fontWeight: 600, color: '#555' }}>배송비</td>
                        <td style={{ fontWeight: 600 }}>{order.shipping_fee.toLocaleString('ko-KR')}원</td>
                      </tr>
                    </tfoot>
                  )}
                </ItemTable>

                <InfoRow style={{ marginTop: 12, borderBottom: 'none' }}>
                  <Label>합계</Label>
                  <Value style={{ fontWeight: 700, fontSize: 17 }}>
                    {order.verified_price?.toLocaleString('ko-KR')}원
                  </Value>
                </InfoRow>
              </>
            ) : (
              <>
                <SectionTitle>주문 금액</SectionTitle>
                <InfoRow style={{ borderBottom: 'none' }}>
                  <Label>합계</Label>
                  <Value style={{ fontWeight: 700, fontSize: 17 }}>
                    {order.verified_price?.toLocaleString('ko-KR')}원
                  </Value>
                </InfoRow>
              </>
            )}

            <BtnRow>
              <PdfBtn $primary onClick={() => setShowPdf(true)}>
                발주서 미리보기
              </PdfBtn>
              <PdfBtn onClick={handleDownload}>
                PDF 다운로드
              </PdfBtn>
            </BtnRow>
          </>
        )}
      </ModalBox>

      {showPdf && (
        <PdfPreviewModal
          userId={userId}
          orderId={orderId}
          orderIdStr={orderIdStr}
          onClose={() => setShowPdf(false)}
        />
      )}
    </Overlay>
  );
};

export default OrderDetailModal;
